import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import AdsSidebar from './components/AdsSidebar';
import QuickActionsPanel from './components/QuickActionsPanel';
import SearchSummaryBanner from './components/SearchSummaryBanner';
import PriceAlertBanner from './components/PriceAlertBanner';
import RoundTripFlightCard from './components/RoundTripFlightCard';
import OneWayFlightCard from './components/OneWayFlightCard';
import HotelCard from './components/HotelCard';
import Footer from './components/Footer';
import FlightFooter from './components/FlightFooter';
import SignInModal from './components/SignInModal';
import PopularDestinations from './components/PopularDestinations';
import LimitReachedModal from './components/LimitReachedModal';
import './App.css';
import { saveSearchToHistory } from './utils/searchHistory';
import { initGA, trackPageView, trackSearch, trackBookingClick, trackEvent } from './utils/analytics';
import { getCachedUserAirport } from './utils/geolocation';
import PopularFlightsCarousel from './components/PopularFlightsCarousel';
import HeroSection from './components/HeroSection';
import TripBuilder from './components/TripBuilder';
import HotelQuickOptions from './components/HotelQuickOptions';
import PackageBuilder from './components/PackageBuilder';
import './components/PackageBuilder.css';
import ItineraryBuilder from './components/ItineraryBuilder';
import './components/ItineraryBuilder.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import B2BRouter from './b2b/B2BRouter';


// ═══════════════════════════════════════════════════════════════════════════════
// NEW IMPORTS FOR ALL TRIP TYPES
// ═══════════════════════════════════════════════════════════════════════════════
import ItineraryView from './components/ItineraryView';
import RecommendationsView from './components/RecommendationsView';
import { 
  saveChat, 
  loadChat, 
  migrateGuestChatToUser, 
  DEFAULT_MESSAGES, 
  DEFAULT_FLIGHT_INFO,
  trimMessages 
} from './utils/chatPersistence';
import { useConversation } from './hooks/useConversation';
import { 
  checkSearchLimit, 
  recordSearch, 
  getUsageSummary 
} from './utils/usageTracking';

// ═══════════════════════════════════════════════════════════════════════════════
// SAFE TEXT HELPER - Prevents React Error #31 (Objects are not valid as children)
// ═══════════════════════════════════════════════════════════════════════════════
const safeText = (value, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    if (value.text) return String(value.text);
    if (value.message) return String(value.message);
    if (value.content) return String(value.content);
    if (value.name) return String(value.name);
    if (value.code) return String(value.code);
    if (value.city) return String(value.city);
    if (value.iataCode) return String(value.iataCode);
    if (value.description) return String(value.description);
    if (Array.isArray(value)) {
      return value.map(v => safeText(v)).join(', ');
    }
    try {
      const str = JSON.stringify(value);
      if (str.startsWith('{') || str.startsWith('[')) {
        return fallback;
      }
      return str;
    } catch {
      return fallback;
    }
  }
  return String(value);
};

function App() {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [showSignIn, setShowSignIn] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [chatMessages, setChatMessages] = useState(DEFAULT_MESSAGES);
  const [chatInput, setChatInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [chatLoaded, setChatLoaded] = useState(false);
  const [showResultsPrompt, setShowResultsPrompt] = useState(null);
  // ═══════════════════════════════════════════════════════════════════════════════
  // CONVERSATION HISTORY - Using dedicated hook (fixes stale closure bug)
  // ═══════════════════════════════════════════════════════════════════════════════
  const { 
    getHistory, 
    addUserMessage, 
    addAssistantMessage, 
    clearHistory: clearConversationHistory,
    setHistoryDirect,
    migrateToUser
  } = useConversation(user?.id || null);
  
  const [lastSearchParams, setLastSearchParams] = useState(null);
  const [tripData, setTripData] = useState(null);
  const [segmentResults, setSegmentResults] = useState({});
  const [selectedOptions, setSelectedOptions] = useState({});
  const [hotels, setHotels] = useState([]);
  const [hotelQuickOptions, setHotelQuickOptions] = useState(null);
  const [showPackageBuilder, setShowPackageBuilder] = useState(false);
  const [showItineraryBuilder, setShowItineraryBuilder] = useState(false);

  // ═══════════════════════════════════════════════════════════════════════════════
  // NEW STATE VARIABLES FOR ALL TRIP TYPES
  // ═══════════════════════════════════════════════════════════════════════════════
  const [itineraryData, setItineraryData] = useState(null);
  const [itineraryMessage, setItineraryMessage] = useState('');
  const [recommendationsData, setRecommendationsData] = useState(null);
  const [recommendationsMessage, setRecommendationsMessage] = useState('');
  const [currentUiHint, setCurrentUiHint] = useState('chat');
  // ═══════════════════════════════════════════════════════════════════════════════
  // SPLIT LAYOUT MODE - Switches from 'landing' to 'split' after first search
  // ═══════════════════════════════════════════════════════════════════════════════
  const [layoutMode, setLayoutMode] = useState('landing'); // 'landing' | 'split'
  const [chatPanelWidth, setChatPanelWidth] = useState(380);
  const [searchAttempted, setSearchAttempted] = useState(false); // Track if search was done this session

  const chatMessagesEndRef = useRef(null);
  const scrollChatToBottom = () => {
    if (chatMessagesEndRef.current) {
      chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  };

  useEffect(() => {
    scrollChatToBottom();
  }, [chatMessages, isTyping, isSearching]);
  
  const [pendingSearch, setPendingSearch] = useState(false);
  
  const [flightInfo, setFlightInfo] = useState({
    from: '',
    to: '',
    departDate: '',
    returnDate: '',
    passengers: 1,
    class: 'economy',
    tripType: 'oneway',
    maxPrice: null,
    dateRange: { start: '', end: '' },
    sortPreference: 'best',
    flexible: false
  });

  const [priceRange, setPriceRange] = useState([0, 5000]);
  const [stops, setStops] = useState('any');
  const [departureTime, setDepartureTime] = useState([]);
  const [arrivalTime, setArrivalTime] = useState([]);
  const [airlines, setAirlines] = useState([]);
  const [duration, setDuration] = useState([0, 24]);
  const [sortBy, setSortBy] = useState('best');
  const [flights, setFlights] = useState([]);
  const [allFlights, setAllFlights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isRoundTrip, setIsRoundTrip] = useState(false);
  const [priceAlert, setPriceAlert] = useState(null);

  // ═══════════════════════════════════════════════════════════════════════════════
  // LAYOUT MODE SWITCH - Go to split layout when we have results
  // ═══════════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    const hasResults = allFlights.length > 0 || hotels.length > 0 || tripData || itineraryData || recommendationsData || showPackageBuilder || showItineraryBuilder;
    if (hasResults && layoutMode === 'landing') {
      setLayoutMode('split');
    }
  }, [allFlights, hotels, tripData, itineraryData, recommendationsData, layoutMode, showItineraryBuilder]);

  useEffect(() => {
    if (pendingSearch && flightInfo.from && flightInfo.to && flightInfo.departDate) {
      setPendingSearch(false);
      simulateSearch();
    }
  }, [flightInfo, pendingSearch]);

  useEffect(() => {
    const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
    if (measurementId) {
      initGA(measurementId);
      trackPageView(window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const detectLocation = async () => {
      try {
        const location = await getCachedUserAirport();
        if (location) {
          setUserLocation(location);
        }
      } catch (error) {
        console.error('Location detection failed:', error);
      }
    };
    detectLocation();
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      const userId = session?.user?.id || null;
      const savedChat = await loadChat(userId);
      
      if (savedChat) {
        setChatMessages(savedChat.messages);
        setFlightInfo(savedChat.flightInfo || DEFAULT_FLIGHT_INFO);
        setHistoryDirect(savedChat.conversationHistory || []);
        
        const fi = savedChat.flightInfo;
        if (fi && fi.from && fi.to && fi.departDate) {
          setShowResultsPrompt({
            from: fi.from,
            to: fi.to,
            departDate: fi.departDate,
            returnDate: fi.returnDate,
            tripType: fi.tripType
          });
        }
        
        if (session?.user) {
          const userName = session.user.user_metadata?.full_name || 'traveler';
          setChatMessages(prev => [...prev, {
            id: Date.now(),
            type: 'ai',
            text: `Welcome back, ${userName}! 👋 I've loaded your previous conversation.`,
            timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          }]);
        }
      } else {
        setChatMessages(DEFAULT_MESSAGES);
        if (session?.user) {
          const userName = session.user.user_metadata?.full_name || 'traveler';
          setChatMessages(prev => [...prev, {
            id: Date.now(),
            type: 'ai',
            text: `Welcome, ${userName}! 👋 How can I help you find a flight today?`,
            timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          }]);
        }
      }
      setChatLoaded(true);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (event === 'SIGNED_IN' && session?.user) {
        setShowSignIn(false);
        const user = session.user;
        const userName = user.user_metadata?.full_name || user.user_metadata?.name || 'traveler';
        const migratedChat = await migrateGuestChatToUser(user.id);
        // Also migrate conversation history from the hook's storage
        await migrateToUser(user.id);
        
        if (migratedChat) {
          setChatMessages(prev => [...prev, {
            id: Date.now(),
            type: 'ai',
            text: `Welcome, ${userName}! 👋 I've saved your conversation to your account.`,
            timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          }]);
        }
      }
      
      if (event === 'SIGNED_OUT') {
        setChatMessages(DEFAULT_MESSAGES);
        setFlightInfo(DEFAULT_FLIGHT_INFO);
        clearConversationHistory();
        setChatLoaded(true);
        setSearchAttempted(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const saveTimeoutRef = useRef(null);
  
  useEffect(() => {
    if (!chatLoaded) return;
    if (chatMessages.length <= DEFAULT_MESSAGES.length) return;
    
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(async () => {
      const userId = user?.id || null;
      const trimmedMessages = trimMessages(chatMessages);
      // getHistory() is synchronous and always returns current history
      await saveChat(trimmedMessages, flightInfo, getHistory(), userId);
    }, 1000);
    
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [chatMessages, flightInfo, user, chatLoaded, getHistory]);

  useEffect(() => {
    if (allFlights.length > 0) {
      const getPrice = (flight) => {
        if (!flight) return 0;
        if (typeof flight.totalPrice === 'number') return flight.totalPrice;
        if (typeof flight.price === 'number') return flight.price;
        if (typeof flight.price === 'object' && flight.price !== null) {
          return flight.price.total || flight.price.perPerson || flight.price.amount || 0;
        }
        return 0;
      };

      const maxPrice = Math.max(...allFlights.map(f => getPrice(f)));
      if (maxPrice > priceRange[1]) {
        const newMax = Math.ceil(maxPrice / 500) * 500;
        setPriceRange([0, newMax]);
      }
    }
  }, [allFlights]);

  useEffect(() => {
    if (allFlights.length > 0) {
      try {
        let filtered = [...allFlights];

        const getFlightPrice = (flight) => {
          if (!flight) return 0;
          if (typeof flight.totalPrice === 'number') return flight.totalPrice;
          if (typeof flight.price === 'number') return flight.price;
          if (typeof flight.price === 'object' && flight.price !== null) {
            return flight.price.total || flight.price.perPerson || flight.price.amount || 0;
          }
          const parsed = parseFloat(flight.price);
          return isNaN(parsed) ? 0 : parsed;
        };

        const getAirlineName = (airline) => {
          if (!airline) return '';
          if (typeof airline === 'string') return airline;
          if (typeof airline === 'object') {
            return airline.name || airline.code || '';
          }
          return String(airline);
        };

        const normalizeStops = (stops) => {
          if (stops === null || stops === undefined) return 'Nonstop';
          if (typeof stops === 'string') return stops;
          if (typeof stops === 'number') {
            if (stops === 0) return 'Nonstop';
            if (stops === 1) return '1 stop';
            return `${stops} stops`;
          }
          return 'Nonstop';
        };

        const stopsMatch = (flightStops, filterValue) => {
          const normalized = normalizeStops(flightStops);
          if (filterValue === 'nonstop') {
            return normalized === 'Nonstop' || normalized === '0 stops';
          }
          if (filterValue === '1stop') {
            return normalized === 'Nonstop' || normalized === '1 stop' || normalized === '0 stops';
          }
          return true;
        };

        const parseHour = (timeStr) => {
          if (!timeStr) return -1;
          if (timeStr.includes('T')) {
            try {
              const date = new Date(timeStr);
              return date.getHours();
            } catch { }
          }
          const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
          if (timeMatch) {
            let hour = parseInt(timeMatch[1]);
            const isPM = timeMatch[3].toUpperCase() === 'PM';
            if (isPM && hour !== 12) hour += 12;
            else if (!isPM && hour === 12) hour = 0;
            return hour;
          }
          const match24 = timeStr.match(/^(\d{1,2}):/);
          if (match24) return parseInt(match24[1]);
          return -1;
        };

        const parseDurationHours = (durationStr) => {
          if (!durationStr) return 0;
          const isoMatch = durationStr.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
          if (isoMatch) {
            return parseInt(isoMatch[1] || 0);
          }
          const formattedMatch = durationStr.match(/(\d+)h/);
          if (formattedMatch) {
            return parseInt(formattedMatch[1]);
          }
          return 0;
        };

        filtered = filtered.filter(f => {
          const flightPrice = getFlightPrice(f);
          return flightPrice >= priceRange[0] && flightPrice <= priceRange[1];
        });

        if (stops === 'nonstop') {
          filtered = filtered.filter(f => {
            if (f.type === 'roundtrip') {
              const outboundStops = f.outboundFlight?.stops ?? f.outboundFlight?.stopsCount ?? f.outbound?.stops;
              const returnStops = f.returnFlight?.stops ?? f.returnFlight?.stopsCount ?? f.return?.stops;
              return stopsMatch(outboundStops, 'nonstop') && stopsMatch(returnStops, 'nonstop');
            }
            const flightStops = f.stops ?? f.stopsCount ?? f.outbound?.stops;
            return stopsMatch(flightStops, 'nonstop');
          });
        } else if (stops === '1stop') {
          filtered = filtered.filter(f => {
            if (f.type === 'roundtrip') {
              const outboundStops = f.outboundFlight?.stops ?? f.outboundFlight?.stopsCount ?? f.outbound?.stops;
              const returnStops = f.returnFlight?.stops ?? f.returnFlight?.stopsCount ?? f.return?.stops;
              return stopsMatch(outboundStops, '1stop') && stopsMatch(returnStops, '1stop');
            }
            const flightStops = f.stops ?? f.stopsCount ?? f.outbound?.stops;
            return stopsMatch(flightStops, '1stop');
          });
        }

        if (airlines.length > 0) {
          filtered = filtered.filter(f => {
            if (f.type === 'roundtrip') {
              const outAirline = getAirlineName(f.outboundFlight?.airline || f.airline);
              const retAirline = getAirlineName(f.returnFlight?.airline || f.airline);
              return airlines.includes(outAirline) || airlines.includes(retAirline);
            }
            const airlineName = getAirlineName(f.airline);
            return airlines.includes(airlineName);
          });
        }

        if (Array.isArray(departureTime) && departureTime.length > 0) {
          filtered = filtered.filter(f => {
            const departureStr = f.type === 'roundtrip' 
              ? (f.outboundFlight?.departure || f.outboundFlight?.departureTime || f.outbound?.departure)
              : (f.departure || f.departureTime);
            const hour = parseHour(departureStr);
            if (hour < 0) return true;
            return departureTime.some(timeSlot => {
              if (timeSlot === 'morning') return hour >= 5 && hour < 12;
              if (timeSlot === 'afternoon') return hour >= 12 && hour < 18;
              if (timeSlot === 'evening') return hour >= 18 && hour < 24;
              if (timeSlot === 'night') return hour >= 0 && hour < 5;
              return true;
            });
          });
        }

        if (duration[1] < 24) {
          filtered = filtered.filter(f => {
            const durationStr = f.type === 'roundtrip' 
              ? (f.outboundFlight?.duration || f.outbound?.duration)
              : f.duration;
            const hours = parseDurationHours(durationStr);
            if (hours === 0) return true;
            return hours <= duration[1];
          });
        }

        filtered = sortFlights(filtered, sortBy);
        setFlights(filtered);
      } catch (error) {
        console.error('Filter error:', error);
        setFlights(allFlights);
      }
    } else {
      setFlights([]);
    }
  }, [priceRange, stops, airlines, departureTime, arrivalTime, duration, sortBy, allFlights]);

  const sortFlights = (flightList, sortType) => {
    const sorted = [...flightList];
    
    const getPrice = (flight) => {
      if (!flight) return 999999;
      if (typeof flight.totalPrice === 'number') return flight.totalPrice;
      if (typeof flight.price === 'number') return flight.price;
      if (typeof flight.price === 'object' && flight.price !== null) {
        return flight.price.total || flight.price.perPerson || flight.price.amount || 999999;
      }
      return 999999;
    };
    
    const getDuration = (dur) => {
      if (!dur) return 999999;
      const isoMatch = dur.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
      if (isoMatch) {
        return (parseInt(isoMatch[1] || 0) * 60) + parseInt(isoMatch[2] || 0);
      }
      const hours = dur.match(/(\d+)h/);
      const mins = dur.match(/(\d+)m/);
      return (parseInt(hours?.[1] || 0) * 60) + parseInt(mins?.[1] || 0);
    };
    
    switch(sortType) {
      case 'cheapest':
        return sorted.sort((a, b) => getPrice(a) - getPrice(b));
      case 'fastest':
        return sorted.sort((a, b) => {
          const durA = a.totalDuration || a.duration || a.outboundFlight?.duration || a.outbound?.duration;
          const durB = b.totalDuration || b.duration || b.outboundFlight?.duration || b.outbound?.duration;
          return getDuration(durA) - getDuration(durB);
        });
      case 'best':
      default:
        return sorted.sort((a, b) => {
          const scoreA = getPrice(a) + (a.prediction === 'up' ? 50 : a.prediction === 'down' ? -50 : 0);
          const scoreB = getPrice(b) + (b.prediction === 'up' ? 50 : b.prediction === 'down' ? -50 : 0);
          return scoreA - scoreB;
        });
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // PROCESS FLIGHTS BY INTENT - DO NOT REMOVE
  // ═══════════════════════════════════════════════════════════════════════════════
  const processFlightsByIntent = (flights, searchParams) => {
    if (!flights || flights.length === 0) return { flights: [], message: null };
    
    let processed = [...flights];
    const intent = searchParams?.intent || 'all';
    const resultLimit = searchParams?.resultLimit || null;
    const stopsFilter = searchParams?.stops || 'any';
    const timeFilter = searchParams?.timePreference || null;
    
    // Helper: Get stops count
    const getStops = (f) => {
      if (f.outbound && typeof f.outbound.stops === 'number') return f.outbound.stops;
      if (typeof f.stops === 'number') return f.stops;
      if (f.stops === 'Nonstop' || f.stops === 'nonstop') return 0;
      const match = String(f.stops).match(/(\d+)/);
      return match ? parseInt(match[1]) : 0;
    };
    
    // Helper: Get departure hour
    const getDepHour = (f) => {
      const depTime = f.outbound?.departure || f.departureTime || f.departure;
      if (!depTime) return -1;
      if (depTime.includes('T')) {
        return parseInt(depTime.split('T')[1].split(':')[0]);
      }
      const match = depTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      if (match) {
        let hour = parseInt(match[1]);
        if (match[3].toUpperCase() === 'PM' && hour !== 12) hour += 12;
        else if (match[3].toUpperCase() === 'AM' && hour === 12) hour = 0;
        return hour;
      }
      return -1;
    };
    
    // Apply stops filter
    if (stopsFilter === 'nonstop') {
      processed = processed.filter(f => getStops(f) === 0);
    } else if (stopsFilter === '1stop') {
      processed = processed.filter(f => getStops(f) <= 1);
    }
    
    // Apply time preference filter
    if (timeFilter) {
      processed = processed.filter(f => {
        const hour = getDepHour(f);
        if (hour === -1) return true;
        switch (timeFilter) {
          case 'morning': return hour >= 5 && hour < 12;
          case 'afternoon': return hour >= 12 && hour < 18;
          case 'evening': return hour >= 18 && hour < 24;
          case 'night': return hour >= 0 && hour < 5;
          default: return true;
        }
      });
    }
    
    // Sort by intent
    if (intent === 'cheapest') {
      processed = sortFlights(processed, 'cheapest');
    } else if (intent === 'fastest') {
      processed = sortFlights(processed, 'fastest');
    } else if (intent === 'best') {
      processed = sortFlights(processed, 'best');
    }
    // 'all' = keep default order (already sorted by price from API)
    
    // Apply result limit
    const limited = resultLimit && resultLimit > 0 ? processed.slice(0, resultLimit) : processed;
    
    // Generate contextual message
    let message = null;
    if (intent !== 'all' && resultLimit) {
      const intentLabels = { cheapest: 'cheapest', fastest: 'fastest', best: 'best value' };
      const stopsLabels = { nonstop: 'nonstop ', '1stop': 'up to 1-stop ', any: '' };
      const timeLabels = { morning: 'morning ', afternoon: 'afternoon ', evening: 'evening ', night: 'night ' };
      
      message = `Here are the ${limited.length} ${intentLabels[intent] || ''} ${stopsLabels[stopsFilter] || ''}${timeLabels[timeFilter] || ''}flights I found:`;
    }
    
    return { flights: limited, allFlights: processed, message };
  };

  const toggleAirline = (airline) => {
    setAirlines(prev => 
      prev.includes(airline) ? prev.filter(a => a !== airline) : [...prev, airline]
    );
  };

  const toggleDepartureTime = (time) => {
    setDepartureTime(prev =>
      prev.includes(time) ? prev.filter(t => t !== time) : [...prev, time]
    );
  };

  const clearFilters = () => {
    const getPrice = (flight) => {
      if (!flight) return 0;
      if (typeof flight.totalPrice === 'number') return flight.totalPrice;
      if (typeof flight.price === 'number') return flight.price;
      if (typeof flight.price === 'object' && flight.price !== null) {
        return flight.price.total || flight.price.perPerson || flight.price.amount || 0;
      }
      return 0;
    };
    const maxPrice = allFlights.length > 0 
      ? Math.ceil(Math.max(...allFlights.map(f => getPrice(f))) / 500) * 500 
      : 5000;
    
    setPriceRange([0, Math.max(maxPrice, 5000)]);
    setStops('any');
    setAirlines([]);
    setDepartureTime([]);
    setDuration([0, 24]);
  };

  const hasActiveFilters = () => {
    return priceRange[1] < 5000 || stops !== 'any' || airlines.length > 0 || departureTime.length > 0 || duration[1] < 24;
  };

  const checkLimitAndProceed = async (searchData = null) => {
    try {
      const usage = await checkSearchLimit(user);
      setUsageData(usage);
      if (!usage.canSearch) {
        setShowLimitModal(true);
        return false;
      }
      await recordSearch(user, searchData);
      setUsageData(prev => ({
        ...prev,
        used: (prev?.used || 0) + 1,
        remaining: prev?.remaining === 'Unlimited' ? 'Unlimited' : Math.max(0, (prev?.remaining || 0) - 1)
      }));
      return true;
    } catch (error) {
      console.error('Error checking search limit:', error);
      return true;
    }
  };

  const handlePopularFlightSelect = async (flightData) => {
    const searchDataForTracking = {
      origin: flightData.origin,
      destination: flightData.destination,
      departureDate: flightData.departureDate,
      returnDate: flightData.returnDate,
      passengers: flightData.travelers || 1,
      travelClass: 'ECONOMY'
    };
    
    const canProceed = await checkLimitAndProceed(searchDataForTracking);
    if (!canProceed) return;
    
    setChatMessages(prev => [...prev, {
      id: Date.now(),
      type: 'ai',
      text: `Got it! Searching flights from ${safeText(flightData.origin)} to ${safeText(flightData.destinationCity)}...`,
      timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    }]);
    
    setFlightInfo({
      from: safeText(flightData.origin),
      to: safeText(flightData.destination),
      departDate: safeText(flightData.departureDate),
      returnDate: safeText(flightData.returnDate),
      passengers: flightData.travelers || 1,
      class: flightData.class?.toLowerCase() || 'economy',
      tripType: 'roundtrip'
    });
    
    setLastSearchParams({
      origin: safeText(flightData.origin),
      destination: safeText(flightData.destination),
      departureDate: safeText(flightData.departureDate),
      returnDate: safeText(flightData.returnDate),
      passengers: flightData.travelers || 1,
      travelClass: 'ECONOMY'
    });
    
    setIsSearching(true);
    setSearchAttempted(true);
    
    try {
      const searchPayload = {
        origin: safeText(flightData.origin),
        destination: safeText(flightData.destination),
        departureDate: safeText(flightData.departureDate),
        returnDate: safeText(flightData.returnDate),
        adults: flightData.travelers || 1,
        travelClass: 'ECONOMY',
        nonStop: false
      };
      
      const searchResponse = await fetch('/api/search-flights', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(searchPayload)
      });
      
      if (searchResponse.ok) {
        const searchData = await searchResponse.json();
        if (searchData.success && searchData.flights && searchData.flights.length > 0) {
          setIsRoundTrip(true);
          setFlights(searchData.flights);
          setAllFlights(searchData.flights);
          setShowResultsPrompt(null);
          setCurrentUiHint('flight_results');
        }
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleShowResultsAgain = () => {
    if (!showResultsPrompt) return;
    setChatMessages(prev => [...prev, {
      id: Date.now(),
      type: 'ai',
      text: `Searching for flights from ${safeText(showResultsPrompt.from)} to ${safeText(showResultsPrompt.to)}...`,
      timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    }]);
    setShowResultsPrompt(null);
    simulateSearch();
  };

  const handleSendMessage = async () => {
    if (!chatInput.trim() || isTyping) return;
    
    const userMessage = chatInput;
    setChatInput('');
    
    setChatMessages(prev => [...prev, {
      id: Date.now(),
      type: 'user',
      text: userMessage,
      timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    }]);

    // ═══════════════════════════════════════════════════════════════════════════════
    // CONVERSATION HISTORY - Add user message BEFORE API call (fixes stale closure)
    // ═══════════════════════════════════════════════════════════════════════════════
    addUserMessage(userMessage);

    setIsTyping(true);
    setItineraryData(null);
    setRecommendationsData(null);
    setShowPackageBuilder(false);
    setShowItineraryBuilder(false);

    try {
      // ═══════════════════════════════════════════════════════════════════════════════
      // CRITICAL: getHistory() returns CURRENT history (never stale)
      // ═══════════════════════════════════════════════════════════════════════════════
      const orchResponse = await fetch('/api/chat-orchestrator', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          message: userMessage,
          sessionId: user?.id || 'anonymous',
          conversationHistory: getHistory(),
          userLocation: userLocation
        })
      });
      
      // Handle different response statuses gracefully
      if (!orchResponse.ok) {
        const status = orchResponse.status;
        
        // Try to get error message from response body
        let errorData = {};
        try {
          errorData = await orchResponse.json();
        } catch {
          // Response might not be JSON
        }
        
        // Handle 504 timeout specially - show helpful message instead of error
        if (status === 504) {
          setChatMessages(prev => [...prev, {
            id: Date.now() + 1,
            type: 'ai',
            text: "I'm taking a bit longer than expected. Could you provide details one at a time? Let's start with: When would you like to travel?",
            timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          }]);
          setIsTyping(false);
          return;
        }
        
        // Handle 429 rate limit
        if (status === 429) {
          setChatMessages(prev => [...prev, {
            id: Date.now() + 1,
            type: 'ai',
            text: errorData.message || "I'm getting a lot of requests right now. Please wait a moment and try again.",
            timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          }]);
          setIsTyping(false);
          return;
        }
        
        // For other errors, throw to be caught by catch block
        throw new Error(errorData.message || `Orchestrator failed: ${status}`);
      }
      
      const orchData = await orchResponse.json();

      // ═══════════════════════════════════════════════════════════════════════════════
      // CONVERSATION HISTORY - Add assistant response (auto-saves to storage)
      // ═══════════════════════════════════════════════════════════════════════════════
      if (orchData.message) {
        addAssistantMessage(orchData.message);
      }
      
      if (orchData.searchParams) {
        setLastSearchParams(orchData.searchParams);
      }

      // HANDLE ERROR ACTION - When orchestrator returns an error
      if (orchData.action === 'error' || !orchData.success) {
        setChatMessages(prev => [...prev, {
          id: Date.now() + 1,
          type: 'ai',
          text: safeText(orchData.message, "Something went wrong. Let's try again - what would you like to plan?"),
          timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
        }]);
        setCurrentUiHint('chat');
        setIsTyping(false);
        return;
      }

      // CASE 0: Just chatting
      if (orchData.action === 'chat') {
        setChatMessages(prev => [...prev, {
          id: Date.now() + 1,
          type: 'ai',
          text: safeText(orchData.message, "I'm here to help with your travel plans!"),
          timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
        }]);
        setCurrentUiHint('chat');
        setIsTyping(false);
        return;
      }

      // CASE 1: Need more info
      if (orchData.action === 'ask' || orchData.needsMoreInfo) {
        setChatMessages(prev => [...prev, {
          id: Date.now() + 1,
          type: 'ai',
          text: safeText(orchData.message, "I need more information to help you."),
          timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
        }]);
        setIsTyping(false);
        return;
      }

      // CASE 2: Search flights/hotels
      if (orchData.action === 'search' && orchData.canSearch) {
        const searchType = orchData.searchType || 'flights';
        
        const canProceed = await checkLimitAndProceed({
          origin: safeText(orchData.searchParams?.origin),
          destination: safeText(orchData.searchParams?.destination),
          departureDate: safeText(orchData.searchParams?.departureDate),
        });
        
        if (!canProceed) {
          setIsTyping(false);
          return;
        }
        
        if (orchData.message) {
          setChatMessages(prev => [...prev, {
            id: Date.now() + 1,
            type: 'ai',
            text: safeText(orchData.message),
            timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          }]);
        }
        
        const shouldSearchFlights = (searchType === 'flights' || searchType === 'both') && orchData.searchParams;
        const shouldSearchHotels = (searchType === 'hotels' || searchType === 'both') && orchData.hotelParams;
        
        // Track if we found flights (fixes stale closure issue)
        let foundFlightsCount = 0;
        
        if (shouldSearchFlights) {
          setIsSearching(true);
          setSearchAttempted(true);
          try {
            const searchResponse = await fetch('/api/search-flights', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                origin: safeText(orchData.searchParams.origin),
                destination: safeText(orchData.searchParams.destination),
                departureDate: safeText(orchData.searchParams.departureDate),
                returnDate: safeText(orchData.searchParams.returnDate),
                // ═══════════════════════════════════════════════════════════════
                // PASSENGER BREAKDOWN - DO NOT REMOVE
                // ═══════════════════════════════════════════════════════════════
                adults: orchData.searchParams.adults || orchData.searchParams.passengers || 1,
                children: orchData.searchParams.children || 0,
                infants: orchData.searchParams.infants || 0,
                travelClass: orchData.searchParams.travelClass || 'ECONOMY',
                // ═══════════════════════════════════════════════════════════════
                // PASS NONSTOP FILTER TO API - DO NOT REMOVE
                // ═══════════════════════════════════════════════════════════════
                nonStop: orchData.searchParams.stops === 'nonstop'
              })
            });
            
            if (searchResponse.ok) {
              const searchData = await searchResponse.json();
              if (searchData.success && searchData.flights?.length > 0) {
                foundFlightsCount = searchData.flights.length; // Track for hotel search below
                setIsRoundTrip(!!orchData.searchParams.returnDate);
                
                // ═══════════════════════════════════════════════════════════════
                // PROCESS FLIGHTS BY INTENT - DO NOT REMOVE
                // ═══════════════════════════════════════════════════════════════
                const { flights: processedFlights, allFlights: allProcessedFlights, message: intentMessage } = 
                  processFlightsByIntent(searchData.flights, orchData.searchParams);
                
                setFlights(processedFlights);
                setAllFlights(allProcessedFlights || searchData.flights);
                
                // If there's an intent-based message, add it to chat
                if (intentMessage) {
                  setChatMessages(prev => [...prev, {
                    id: Date.now() + 2,
                    role: 'assistant',
                    content: intentMessage
                  }]);
                }
                
                setCurrentUiHint('flight_results');
                setFlightInfo({
                  from: safeText(orchData.searchParams.origin),
                  to: safeText(orchData.searchParams.destination),
                  departDate: safeText(orchData.searchParams.departureDate),
                  returnDate: safeText(orchData.searchParams.returnDate),
                  passengers: orchData.searchParams.passengers || 1,
                  class: orchData.searchParams.travelClass?.toLowerCase() || 'economy',
                  tripType: orchData.searchParams.returnDate ? 'roundtrip' : 'oneway'
                });
              }
            }
          } catch (err) {
            console.error('Flight search error:', err);
          }
          setIsSearching(false);
        }
        
        if (shouldSearchHotels) {
          try {
            const hotelResponse = await fetch('/api/search-flights', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                type: 'hotels',
                city: safeText(orchData.hotelParams.city),
                checkIn: safeText(orchData.hotelParams.checkIn),
                checkOut: safeText(orchData.hotelParams.checkOut),
                guests: orchData.hotelParams.guests || 2,
              })
            });
            
            if (hotelResponse.ok) {
              const hotelData = await hotelResponse.json();
              if (hotelData.success && hotelData.hotels?.length > 0) {
                setHotels(hotelData.hotels);
                setHotelQuickOptions(hotelData.quickOptions || null);
                
                // Check if we have BOTH flights and hotels for Package Builder
                // Use foundFlightsCount (local var) instead of flights.length (stale state)
                if (foundFlightsCount > 0) {
                  setShowPackageBuilder(true);
                  setCurrentUiHint('package_builder');
                } else {
                  setCurrentUiHint('hotel_results');
                }
              }

            }
          } catch (err) {
            console.error('Hotel search error:', err);
          }
        }
        
        setLoading(false);
        setIsTyping(false);
        return;
      }

      // CASE 3: Build Trip
      if (orchData.action === 'build_trip') {
        if (orchData.message) {
          setChatMessages(prev => [...prev, {
            id: Date.now() + 1,
            type: 'ai',
            text: safeText(orchData.message),
            timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          }]);
        }
        
        if (orchData.tripData) {
          setTripData(orchData.tripData);
          setSegmentResults({});
          setSelectedOptions({});
          setCurrentUiHint('trip_builder');
        }
        
        setIsTyping(false);
        return;
      }

      // CASE 4: Itinerary View (Single City)
      if (orchData.action === 'itinerary') {
        if (orchData.message) {
          setChatMessages(prev => [...prev, {
            id: Date.now() + 1,
            type: 'ai',
            text: safeText(orchData.message),
            timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          }]);
        }
        
        // Check if message contains actual itinerary (Day 1, Day 2, etc.) or just questions
        const messageText = safeText(orchData.message);
        const hasActualItinerary = /Day\s*1[:\s-]/i.test(messageText) || 
                                    /\*\*Day\s*1/i.test(messageText) ||
                                    /##?\s*Day\s*1/i.test(messageText);
        const isJustAskingQuestions = /could you tell me|are you traveling|what kind of pace|do you prefer/i.test(messageText);
        
        // Check if this is a multi-city trip (uses ItineraryBuilder)
        const isMultiCity = orchData.itineraryData?.cities?.length > 1 || 
                            orchData.itineraryData?.isMultiCity ||
                            orchData.itineraryData?.destinations?.length > 1;
        
        // Only show views if there's actual day-by-day content
        if (hasActualItinerary && !isJustAskingQuestions) {
          if (isMultiCity) {
            // Use ItineraryBuilder for multi-city trips
            setItineraryData(orchData.itineraryData || {});
            setShowItineraryBuilder(true);
            setCurrentUiHint('itinerary_builder');
          } else {
            // Use simple ItineraryView for single-city trips
            setItineraryData(orchData.itineraryData || {});
            setItineraryMessage(safeText(orchData.message));
            setCurrentUiHint('itinerary_view');
          }
          setTripData(null);
          setRecommendationsData(null);
          setFlights([]);
          setAllFlights([]);
        } else {
          // AI is asking questions - just show in chat, no special UI
          setCurrentUiHint('chat');
        }
        
        setIsTyping(false);
        return;
      }

      // CASE 4B: Multi-City Itinerary Planning (NEW - ItineraryBuilder)
      if (orchData.action === 'plan_itinerary') {
        if (orchData.message) {
          setChatMessages(prev => [...prev, {
            id: Date.now() + 1,
            type: 'ai',
            text: safeText(orchData.message),
            timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          }]);
        }
        
        // Set itinerary data and show ItineraryBuilder
        if (orchData.itineraryData && orchData.itineraryData.cities?.length > 0) {
          setItineraryData({
            ...orchData.itineraryData,
            isMultiCity: true
          });
          setShowItineraryBuilder(true);
          setCurrentUiHint('itinerary_builder');
          
          // Clear other views
          setTripData(null);
          setRecommendationsData(null);
          setShowPackageBuilder(false);
          setFlights([]);
          setAllFlights([]);
          setHotels([]);
        }
        
        setIsTyping(false);
        return;
      }

      // CASE 5: Recommendations View
      if (orchData.action === 'recommendations') {
        if (orchData.message) {
          setChatMessages(prev => [...prev, {
            id: Date.now() + 1,
            type: 'ai',
            text: safeText(orchData.message),
            timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          }]);
        }
        
        // Check if message contains actual recommendations
        const messageText = safeText(orchData.message);
        console.log('🌍 Recommendations response:', messageText?.substring(0, 300));
        
        // More lenient detection - look for numbered lists or destination indicators
        const hasActualRecommendations = 
          /\d+[\.\)]\s*\*{0,2}[A-Z]/i.test(messageText) || // "1. Algarve" or "1) **Bali**"
          /here are|top picks|recommend|destinations|places/i.test(messageText) ||
          /algarve|amalfi|greece|greek|bali|thailand|cancun|caribbean/i.test(messageText); // Common destinations
        
        const isJustAskingQuestions = 
          /what.*looking for|could you tell|what.*prefer|when.*planning/i.test(messageText) &&
          !hasActualRecommendations;
        
        // Only show RecommendationsView if there's actual destination content
        if (hasActualRecommendations && !isJustAskingQuestions) {
          setRecommendationsData(orchData.recommendationsData || {});
          setRecommendationsMessage(safeText(orchData.message));
          setCurrentUiHint('recommendations_view');
          setTripData(null);
          setItineraryData(null);
          setFlights([]);
          setAllFlights([]);
        } else {
          // AI is asking questions - just show in chat, no special UI
          console.log('🌍 No destinations detected, showing chat only');
          setCurrentUiHint('chat');
        }
        
        setIsTyping(false);
        return;
      }

      // CASE 6: Modify
      if (orchData.action === 'modify') {
        if (orchData.message) {
          setChatMessages(prev => [...prev, {
            id: Date.now() + 1,
            type: 'ai',
            text: safeText(orchData.message),
            timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          }]);
        }
        
        if (orchData.searchParams) {
          setIsSearching(true);
          setSearchAttempted(true);
          try {
            const searchResponse = await fetch('/api/search-flights', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                origin: safeText(orchData.searchParams.origin),
                destination: safeText(orchData.searchParams.destination),
                departureDate: safeText(orchData.searchParams.departureDate),
                returnDate: safeText(orchData.searchParams.returnDate),
                adults: orchData.searchParams.passengers || 1,
              })
            });
            
            if (searchResponse.ok) {
              const searchData = await searchResponse.json();
              if (searchData.success && searchData.flights?.length > 0) {
                setFlights(searchData.flights);
                setAllFlights(searchData.flights);
                setCurrentUiHint('flight_results');
              }
            }
          } catch (err) {
            console.error('Modify search error:', err);
          }
          setIsSearching(false);
        }
        
        setIsTyping(false);
        return;
      }

      // CASE 7: Add Hotels
      if (orchData.action === 'add_hotels') {
        if (orchData.message) {
          setChatMessages(prev => [...prev, {
            id: Date.now() + 1,
            type: 'ai',
            text: safeText(orchData.message),
            timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          }]);
        }
        
        if (orchData.hotelParams) {
          setIsSearching(true);
          const hotelsToSearch = orchData.hotelParams.allHotels || [orchData.hotelParams];
          const foundHotels = [];
          
          for (const hotel of hotelsToSearch) {
            try {
              const hotelResponse = await fetch('/api/search-flights', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  type: 'hotels',
                  city: safeText(hotel.city),
                  checkIn: safeText(hotel.checkIn),
                  checkOut: safeText(hotel.checkOut),
                  guests: hotel.guests || 2,
                })
              });
              
              if (hotelResponse.ok) {
                const hotelData = await hotelResponse.json();
                if (hotelData.success && hotelData.hotels?.length > 0) {
                  foundHotels.push(...hotelData.hotels);
                }
              }
            } catch (err) {
              console.error('Hotel search error:', err);
            }
          }
          
          if (foundHotels.length > 0) {
            setHotels(foundHotels);
          }
          setIsSearching(false);
        }
        
        setIsTyping(false);
        return;
      }

    } catch (error) {
      console.error('Error:', error);
      
      // Determine error message based on error type
      let errorMessage = 'Sorry, I encountered an error. Please try again.';
      
      if (error.message?.includes('504') || error.message?.includes('timeout')) {
        errorMessage = "That took a bit too long. Could you try providing the details one at a time? For example, start with your travel dates.";
      } else if (error.message?.includes('429')) {
        errorMessage = "I'm getting a lot of requests right now. Please wait a moment and try again.";
      } else if (error.message?.includes('503') || error.message?.includes('overloaded')) {
        errorMessage = "I'm a bit busy right now. Please try again in a few seconds.";
      } else if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
        errorMessage = "I'm having trouble connecting. Please check your internet connection and try again.";
      }
      
      setChatMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'ai',
        text: errorMessage,
        timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
      }]);
    } finally {
      setIsTyping(false);
      setLoading(false);
    }
  };

  // Trip Builder handlers
  const handleSearchSegment = async (segment, index) => {
    setIsSearching(true);
    
    if (segment.type === 'flight') {
      try {
        const response = await fetch('/api/search-flights', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            origin: safeText(segment.from),
            destination: safeText(segment.to),
            departureDate: safeText(segment.date),
            adults: tripData?.travelers?.totalCount || 1,
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.flights?.length > 0) {
            setSegmentResults(prev => ({ ...prev, [index]: data.flights }));
          }
        }
      } catch (err) {
        console.error('Segment search error:', err);
      }
    } else if (segment.type === 'hotel') {
      try {
        const response = await fetch('/api/search-flights', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'hotels',
            city: safeText(segment.city),
            checkIn: safeText(segment.date),
            checkOut: safeText(segment.endDate),
            guests: tripData?.travelers?.totalCount || 2
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.hotels?.length > 0) {
            setSegmentResults(prev => ({ ...prev, [index]: data.hotels }));
          }
        }
      } catch (err) {
        console.error('Hotel segment search error:', err);
      }
    }
    
    setIsSearching(false);
  };

  const handleSearchAllSegments = async () => {
    if (!tripData?.segments) return;
    setIsSearching(true);
    
    for (let i = 0; i < tripData.segments.length; i++) {
      const segment = tripData.segments[i];
      if (segment.needsBooking !== false) {
        await handleSearchSegment(segment, i);
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    
    setIsSearching(false);
  };

  const handleSelectOption = (segmentIndex, optionIndex) => {
    setSelectedOptions(prev => ({ ...prev, [segmentIndex]: optionIndex }));
  };

  const handleAddHotelToTrip = () => {
    setChatInput('Please add hotels to my trip');
    setTimeout(() => document.querySelector('.send-btn')?.click(), 100);
  };

  const handleCloseTripBuilder = () => {
    setTripData(null);
    setSegmentResults({});
    setSelectedOptions({});
    setCurrentUiHint('chat');
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // SPLIT LAYOUT HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════════
  const handleReturnToLanding = () => {
    setLayoutMode('landing');
    setAllFlights([]);
    setFlights([]);
    setHotels([]);
    setTripData(null);
    setItineraryData(null);
    setRecommendationsData(null);
    setCurrentUiHint('chat');
    setSearchAttempted(false); // Reset search flag
  };

  const handleChatResize = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = chatPanelWidth;
    
    const onMouseMove = (moveE) => {
      const newWidth = Math.max(320, Math.min(500, startWidth + (moveE.clientX - startX)));
      setChatPanelWidth(newWidth);
    };
    
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // Itinerary & Recommendations handlers
  const handleCloseItinerary = () => {
    setItineraryData(null);
    setItineraryMessage('');
    setCurrentUiHint('chat');
  };

  const handleCloseRecommendations = () => {
    setRecommendationsData(null);
    setRecommendationsMessage('');
    setCurrentUiHint('chat');
  };

  const handleSelectDestination = (destination) => {
    console.log('Selected destination:', destination);
  };

  const handleSearchFromItinerary = () => {
    if (itineraryData?.destination) {
      setChatInput(`Search flights to ${itineraryData.destination}`);
      setItineraryData(null);
      setCurrentUiHint('chat');
      setTimeout(() => document.querySelector('.send-btn')?.click(), 100);
    }
  };

  const handleSearchFromRecommendation = (destination) => {
    if (destination?.name) {
      setChatInput(`Search flights to ${destination.name}`);
      setRecommendationsData(null);
      setCurrentUiHint('chat');
      setTimeout(() => document.querySelector('.send-btn')?.click(), 100);
    }
  };

  const simulateSearch = async () => {
    const canProceed = await checkLimitAndProceed({
      origin: safeText(flightInfo.from),
      destination: safeText(flightInfo.to),
    });
    if (!canProceed) return;
    
    setLoading(true);
    setIsSearching(true);
    
    try {
      if (!flightInfo.from || !flightInfo.to || !flightInfo.departDate) {
        throw new Error('Missing search parameters');
      }

      const origin = getAirportCode(safeText(flightInfo.from));
      const destination = getAirportCode(safeText(flightInfo.to));
      const departureDate = formatDateForAPI(safeText(flightInfo.departDate));
      const returnDate = flightInfo.tripType === 'roundtrip' ? formatDateForAPI(safeText(flightInfo.returnDate)) : null;

      const response = await fetch('/api/search-flights', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          origin,
          destination,
          departureDate,
          returnDate,
          adults: flightInfo.passengers || 1,
          travelClass: flightInfo.class || 'economy',
        }),
      });

      const data = await response.json();
      
      if (data.success && data.flights?.length > 0) {
        setAllFlights(data.flights);
        setFlights(data.flights);
        setShowResultsPrompt(null);
        setCurrentUiHint('flight_results');
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  const getAirportCode = (city) => {
    if (!city || city.trim() === '') return null;
    const cityTrimmed = city.trim();
    if (cityTrimmed.length === 3 && /^[A-Z]{3}$/.test(cityTrimmed.toUpperCase())) {
      return cityTrimmed.toUpperCase();
    }
    const airportCodes = {
      'new york': 'JFK', 'nyc': 'JFK', 'london': 'LHR', 'los angeles': 'LAX',
      'la': 'LAX', 'chicago': 'ORD', 'boston': 'BOS', 'miami': 'MIA',
      'san francisco': 'SFO', 'sf': 'SFO', 'paris': 'CDG', 'tokyo': 'NRT',
      'dubai': 'DXB', 'houston': 'IAH', 'delhi': 'DEL', 'mumbai': 'BOM',
      'singapore': 'SIN', 'hong kong': 'HKG', 'sydney': 'SYD', 'toronto': 'YYZ',
    };
    const code = airportCodes[cityTrimmed.toLowerCase()];
    if (code) return code;
    if (cityTrimmed.length >= 3) return cityTrimmed.substring(0, 3).toUpperCase();
    return null;
  };

  const formatDateForAPI = (dateStr) => {
    if (!dateStr) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    try {
      const parsedDate = new Date(dateStr);
      if (isNaN(parsedDate.getTime())) return null;
      return parsedDate.toISOString().split('T')[0];
    } catch {
      return null;
    }
  };

  const formatDateDDMM = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      return `${String(date.getDate()).padStart(2, '0')}${String(date.getMonth() + 1).padStart(2, '0')}`;
    } catch {
      return '';
    }
  };

  const handleOAuthSignIn = async (provider) => {
    try {
      await supabase.auth.signInWithOAuth({
        provider: provider,
        options: { redirectTo: 'https://airgati.com' }
      });
    } catch (error) {
      console.error('Sign in error:', error);
    }
  };

  const handleModifySearch = (updatedFlightInfo) => {
    setFlightInfo(updatedFlightInfo);
    setLoading(true);
    setTimeout(() => simulateSearch(), 500);
  };

  return (
    <div className={`app ${layoutMode === 'split' ? 'split-mode' : 'landing-mode'}`}>
      {/* Header */}
      <div className="header">
        <div className="header-content">
          <div className="logo" onClick={layoutMode === 'split' ? handleReturnToLanding : undefined} style={layoutMode === 'split' ? { cursor: 'pointer' } : {}}>
            <img src="/logo.png" alt="Airgati" className="logo-image" />
          </div>
          <div className="nav">
            <a href="/features">Features</a>
            <a href="/pricing">Pricing</a>
          </div>
          <div className="header-actions">
            {user ? (
              <a href="/account" className="user-account-btn" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                <span className="user-icon">👤</span>
                My Account
              </a>
            ) : (
              <button className="sign-in-btn" onClick={() => setShowSignIn(true)}>Sign In</button>
            )}
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════════════
           LANDING MODE - Original centered layout
           ═══════════════════════════════════════════════════════════════════════════════ */}
      {layoutMode === 'landing' && (
      <div className="main-container">
        <div className="content-area">
          
          {/* Hero Section - Only show when no results */}
          {allFlights.length === 0 && !tripData && !itineraryData && !recommendationsData && (
            <HeroSection userLocation={userLocation} onFlightSelect={handlePopularFlightSelect} />
          )}

          {/* AI Chat Interface */}
          <div className="ai-chat-container">
            <div className="ai-chat-header">
              <div className="ai-avatar">🌍</div>
              <div className="ai-greeting">
                <h2>Your Travel Buddy</h2>
                <p>Let's plan your next adventure</p>
              </div>
              <div className="status-indicator">
                <span className="status-dot"></span>
                Online
              </div>
            </div>

            <div className="chat-messages">
              {chatMessages.map((message) => (
                <div key={message.id} className={`message ${message.type}`}>
                  <div className="message-content">
                    <div className="message-text">{safeText(message.text)}</div>
                    {message.searchLinks && Object.keys(message.searchLinks).length > 0 && (
                      <div className="search-links-container">
                        <div className="search-links-buttons">
                          {message.searchLinks.aviasales && (
                            <a href={message.searchLinks.aviasales} target="_blank" rel="noopener noreferrer" className="search-link-btn">✈️ Aviasales</a>
                          )}
                          {message.searchLinks.kayak && (
                            <a href={message.searchLinks.kayak} target="_blank" rel="noopener noreferrer" className="search-link-btn">🔍 Kayak</a>
                          )}
                          {message.searchLinks.google && (
                            <a href={message.searchLinks.google} target="_blank" rel="noopener noreferrer" className="search-link-btn">🌐 Google Flights</a>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="message-timestamp">{safeText(message.timestamp)}</div>
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="message ai">
                  <div className="message-content">
                    <div className="typing-indicator"><span></span><span></span><span></span></div>
                  </div>
                </div>
              )}
              {isSearching && (
                <div className="message ai searching-message">
                  <div className="message-content">
                    <div className="searching-indicator">
                      <div className="plane-loading">✈️</div>
                      <div className="searching-text">
                        <strong>Finding your flights...</strong>
                        <p>{safeText(flightInfo.from)} → {safeText(flightInfo.to)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {showResultsPrompt && !isSearching && (
                <div className="message ai results-prompt-message">
                  <div className="message-content">
                    <div className="results-prompt">
                      <div className="results-prompt-text">
                        <span className="results-prompt-icon">✈️</span>
                        <div>
                          <strong>Previous Search</strong>
                          <p>{safeText(showResultsPrompt.from)} → {safeText(showResultsPrompt.to)}</p>
                        </div>
                      </div>
                      <button className="show-results-btn" onClick={handleShowResultsAgain}>🔄 Show Results Again</button>
                    </div>
                  </div>
                </div>
              )}
              <div ref={chatMessagesEndRef} />
            </div>

            <div className="chat-input-area">
              <div className="input-wrapper">
                <input 
                  type="text" 
                  className="chat-input" 
                  placeholder="Where to next?"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                />
                <button className="voice-btn">🎤</button>
                <button className="send-btn" onClick={handleSendMessage}>↗</button>
              </div>
            </div>
          </div>

          {/* Features, Social Proof, CTA - Only when no results */}
          {allFlights.length === 0 && !tripData && !itineraryData && !recommendationsData && (
            <>
              <div className="features-preview" id="features">
                <h2 className="features-title">Everything you need in one place</h2>
                <p className="features-subtitle">Powered by AI to make travel booking simple, smart, and stress-free</p>
                <div className="features-grid">
                  <div className="feature-card">
                    <div className="feature-icon blue">💬</div>
                    <div className="feature-title">Natural Conversation</div>
                    <div className="feature-description">Just chat naturally—no forms, no confusion. Our AI understands you.</div>
                  </div>
                  <div className="feature-card">
                    <div className="feature-icon amber">📈</div>
                    <div className="feature-title">Price Predictions</div>
                    <div className="feature-description">AI forecasts tell you when prices will rise or drop. Book at the right time.</div>
                  </div>
                  <div className="feature-card">
                    <div className="feature-icon green">⚡</div>
                    <div className="feature-title">Instant Results</div>
                    <div className="feature-description">Search hundreds of flights in seconds. Get real-time prices instantly.</div>
                  </div>
                  <div className="feature-card">
                    <div className="feature-icon purple">💡</div>
                    <div className="feature-title">Smart Suggestions</div>
                    <div className="feature-description">AI suggests destinations based on your interests, budget, and travel style.</div>
                  </div>
                  <div className="feature-card">
                    <div className="feature-icon rose">🛡️</div>
                    <div className="feature-title">Best Price Guarantee</div>
                    <div className="feature-description">We compare prices from 500+ airlines to find you the absolute best deal.</div>
                  </div>
                  <div className="feature-card">
                    <div className="feature-icon cyan">📅</div>
                    <div className="feature-title">Flexible Booking</div>
                    <div className="feature-description">Change your plans? No problem. Easily modify or cancel with no hidden fees.</div>
                  </div>
                </div>
              </div>
              <div className="cta-section">
                <div className="cta-box">
                  <h2 className="cta-title">Ready to travel smarter?</h2>
                  <p className="cta-subtitle">Join thousands who never overpay for flights</p>
                  <button className="cta-button" onClick={() => document.querySelector('.chat-input').focus()}>Start Planning Your Trip →</button>
                </div>
              </div>
            </>
          )}

          {/* Trip Builder */}
          {tripData && (
            <TripBuilder 
              tripData={tripData}
              onSearchSegment={handleSearchSegment}
              onSearchAll={handleSearchAllSegments}
              onSelectOption={handleSelectOption}
              onAddHotel={handleAddHotelToTrip}
              onClose={handleCloseTripBuilder}
              isSearching={isSearching}
              segmentResults={segmentResults}
              selectedOptions={selectedOptions}
            />
          )}

          {/* Itinerary View */}
          {itineraryData && currentUiHint === 'itinerary_view' && (
            <ItineraryView 
              itineraryData={itineraryData}
              message={itineraryMessage}
              onClose={handleCloseItinerary}
              onBookFlights={handleSearchFromItinerary}
              onBookHotels={() => {
                if (itineraryData?.destination) {
                  setChatInput(`Find hotels in ${itineraryData.destination}`);
                  setTimeout(() => document.querySelector('.send-btn')?.click(), 100);
                }
              }}
            />
          )}

          {/* Recommendations View */}
          {recommendationsData && currentUiHint === 'recommendations_view' && (
            <RecommendationsView 
              recommendationsData={recommendationsData}
              message={recommendationsMessage}
              onClose={handleCloseRecommendations}
              onSelectDestination={handleSelectDestination}
              onSearchFlights={handleSearchFromRecommendation}
            />
          )}

          {/* No Results Section - Only show after actual search attempt */}
          {searchAttempted && allFlights.length === 0 && flightInfo.from && flightInfo.to && flightInfo.departDate && !isSearching && !tripData && !itineraryData && !recommendationsData && (
            <section className="no-results-section">
              <div className="no-results-container">
                <div className="no-results-icon">✈️</div>
                <h2>No Cached Prices Available</h2>
                <p className="no-results-route">{safeText(flightInfo.from)} → {safeText(flightInfo.to)}</p>
                <div className="search-live-prices">
                  <p className="search-live-label">🔍 Search live prices on:</p>
                  <div className="search-links-row">
                    <a href={`https://www.aviasales.com/search/${safeText(flightInfo.from)}${formatDateDDMM(flightInfo.departDate)}${safeText(flightInfo.to)}${flightInfo.returnDate ? formatDateDDMM(flightInfo.returnDate) : ''}${flightInfo.passengers || 1}?marker=689249`} target="_blank" rel="noopener noreferrer" className="search-link-btn large">✈️ Aviasales</a>
                    <a href={`https://www.kayak.com/flights/${safeText(flightInfo.from)}-${safeText(flightInfo.to)}/${safeText(flightInfo.departDate)}`} target="_blank" rel="noopener noreferrer" className="search-link-btn large">🔍 Kayak</a>
                    <a href={`https://www.google.com/travel/flights?q=flights%20from%20${safeText(flightInfo.from)}%20to%20${safeText(flightInfo.to)}`} target="_blank" rel="noopener noreferrer" className="search-link-btn large">🌐 Google Flights</a>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Flight Results */}
          {allFlights.length > 0 && (
            <section className="results-section">
              <div className="filters-sidebar">
                <div className="filters-header">
                  <div className="filters-title"><span className="filters-title-icon">⚙️</span>Filters</div>
                  {hasActiveFilters() && <button className="clear-filters-btn" onClick={clearFilters}>Clear All</button>}
                </div>
                <div className="filter-group">
                  <div className="filter-group-header"><span className="filter-group-title">Price Range</span></div>
                  <div className="price-slider-container">
                    <div className="price-display"><span className="price-value">${priceRange[0]}</span><span className="price-separator">—</span><span className="price-value">${priceRange[1]}</span></div>
                    <input type="range" className="custom-range" min="0" max={Math.max(priceRange[1], 5000)} value={priceRange[1]} onChange={(e) => setPriceRange([0, Number(e.target.value)])} />
                  </div>
                </div>
                <div className="filter-divider"></div>
                <div className="filter-group">
                  <div className="filter-group-header"><span className="filter-group-title">Stops</span></div>
                  <div className="filter-options">
                    {['any', 'nonstop', '1stop'].map(s => (
                      <div key={s} className={`filter-option ${stops === s ? 'selected' : ''}`} onClick={() => setStops(s)}>
                        <div className="custom-radio"><div className="custom-radio-inner"></div></div>
                        <span className="option-label">{s === 'any' ? 'Any' : s === 'nonstop' ? 'Nonstop' : '1 Stop'}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="filter-divider"></div>
                {/* ═══════════════════════════════════════════════════════════════════════════════ */}
                {/* AIRLINES FILTER - DO NOT REMOVE */}
                {/* ═══════════════════════════════════════════════════════════════════════════════ */}
                <div className="filter-group">
                  <div className="filter-group-header"><span className="filter-group-title">Airlines</span></div>
                  <div className="filter-options airlines-filter">
                    {(() => {
                      const airlineSet = new Set();
                      allFlights.forEach(f => {
                        const name = typeof f.airline === 'object' ? f.airline.name : f.airline;
                        const code = typeof f.airline === 'object' ? f.airline.code : '';
                        if (name && name !== 'Unknown') airlineSet.add(name);
                        else if (code) airlineSet.add(code);
                      });
                      const uniqueAirlines = Array.from(airlineSet).sort();
                      
                      return uniqueAirlines.slice(0, 8).map(airlineName => (
                        <div 
                          key={airlineName} 
                          className={`filter-option checkbox-option ${airlines.includes(airlineName) ? 'selected' : ''}`}
                          onClick={() => toggleAirline(airlineName)}
                        >
                          <div className={`custom-checkbox ${airlines.includes(airlineName) ? 'checked' : ''}`}>
                            {airlines.includes(airlineName) && <span className="checkmark">✓</span>}
                          </div>
                          <span className="option-label">{airlineName}</span>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
                <div className="filter-divider"></div>
                {/* ═══════════════════════════════════════════════════════════════════════════════ */}
                {/* DEPARTURE TIME FILTER - DO NOT REMOVE */}
                {/* ═══════════════════════════════════════════════════════════════════════════════ */}
                <div className="filter-group">
                  <div className="filter-group-header"><span className="filter-group-title">Departure Time</span></div>
                  <div className="filter-options time-filter">
                    {[
                      { id: 'morning', label: 'Morning', sublabel: '5am - 12pm', icon: '🌅' },
                      { id: 'afternoon', label: 'Afternoon', sublabel: '12pm - 6pm', icon: '☀️' },
                      { id: 'evening', label: 'Evening', sublabel: '6pm - 12am', icon: '🌆' },
                      { id: 'night', label: 'Night', sublabel: '12am - 5am', icon: '🌙' }
                    ].map(time => (
                      <div 
                        key={time.id}
                        className={`filter-option time-option ${departureTime.includes(time.id) ? 'selected' : ''}`}
                        onClick={() => setDepartureTime(prev => 
                          prev.includes(time.id) ? prev.filter(t => t !== time.id) : [...prev, time.id]
                        )}
                      >
                        <div className={`custom-checkbox ${departureTime.includes(time.id) ? 'checked' : ''}`}>
                          {departureTime.includes(time.id) && <span className="checkmark">✓</span>}
                        </div>
                        <span className="time-icon">{time.icon}</span>
                        <div className="time-labels">
                          <span className="option-label">{time.label}</span>
                          <span className="option-sublabel">{time.sublabel}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="filter-divider"></div>
                {/* ═══════════════════════════════════════════════════════════════════════════════ */}
                {/* DURATION FILTER - DO NOT REMOVE */}
                {/* ═══════════════════════════════════════════════════════════════════════════════ */}
                <div className="filter-group">
                  <div className="filter-group-header"><span className="filter-group-title">Max Duration</span></div>
                  <div className="duration-slider-container">
                    <div className="duration-display">
                      <span className="duration-value">Up to {duration[1]} hours</span>
                    </div>
                    <input 
                      type="range" 
                      className="custom-range" 
                      min="1" 
                      max="24" 
                      value={duration[1]} 
                      onChange={(e) => setDuration([0, Number(e.target.value)])} 
                    />
                    <div className="duration-labels">
                      <span>1h</span>
                      <span>24h+</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="results-content">
                <SearchSummaryBanner flightInfo={flightInfo} />
                <QuickActionsPanel flightInfo={flightInfo} flights={flights} onModify={handleModifySearch} />
                <div className="results-header">
                  <h3>{flights.length > 0 ? `${flights.length} Flights Found` : 'No Matching Flights'}</h3>
                  {flights.length > 0 && (
                    <div className="sort-options">
                      {['best', 'cheapest', 'fastest'].map(s => (
                        <button key={s} className={`sort-btn ${sortBy === s ? 'active' : ''}`} onClick={() => setSortBy(s)}>{s.charAt(0).toUpperCase() + s.slice(1)}</button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flights-list">
                  {flights.length === 0 && allFlights.length > 0 && (
                    <div className="no-matching-flights">
                      <div className="no-results-icon">🔍</div>
                      <h4>No matching flights found</h4>
                      <button className="clear-filters-btn-large" onClick={clearFilters}>Clear All Filters</button>
                    </div>
                  )}
                  {flights.length > 0 && isRoundTrip ? (
                    flights.map((flight, index) => (
                      <RoundTripFlightCard key={flight.id || `rt-${index}`} flight={flight} outboundFlight={flight.outboundFlight} returnFlight={flight.returnFlight} lastSearchParams={lastSearchParams || flightInfo} isBestDeal={index === 0} onBook={(data) => trackBookingClick(data, 'expedia')} />
                    ))
                  ) : flights.length > 0 ? (
                    flights.map((flight, index) => (
                      <OneWayFlightCard key={flight.id || `ow-${index}`} flight={flight} lastSearchParams={lastSearchParams || flightInfo} isBestDeal={index === 0} onBook={(data) => trackBookingClick(data.flight, 'expedia')} />
                    ))
                  ) : null}
                </div>
              </div>
            </section>
          )}

          {/* Hotels Section */}
          {hotels.length > 0 && (
            <section className="hotels-results-section">
              <div className="section-container">
                <div className="hotels-header"><h2>🏨 Hotels</h2><span className="hotels-count">{hotels.length} options found</span></div>
                <div className="hotels-list">
                  {hotels.filter(h => h && (h.name || h.hotelName)).map((hotel, index) => (
                    <HotelCard key={hotel.id || `hotel-${index}`} hotel={{...hotel, name: safeText(hotel.name || hotel.hotelName, 'Hotel')}} onBook={(data) => trackEvent('hotel_booking_click', { hotel_name: safeText(hotel.name) })} />
                  ))}
                </div>
              </div>
            </section>
          )}

          <FlightFooter userLocation={userLocation} onRouteClick={(route) => { setChatInput(`I want to fly from ${safeText(route.from)} to ${safeText(route.to)}`); setTimeout(() => document.querySelector('.send-btn')?.click(), 100); }} />
          <Footer />
        </div>
        {flights.length > 0 && <AdsSidebar />}
      </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════════
           SPLIT MODE - 3-column layout after first search
           ═══════════════════════════════════════════════════════════════════════════════ */}
      {layoutMode === 'split' && (
      <div className="split-layout" style={{ '--chat-width': `${chatPanelWidth}px` }}>
        
        {/* LEFT PANEL: Chat */}
        <div className="chat-panel" style={{ width: chatPanelWidth }}>
          <div className="resize-handle" onMouseDown={handleChatResize}></div>
          
          <div className="ai-chat-header">
            <div className="ai-avatar">🌍</div>
            <div className="ai-greeting">
              <h2>Your Travel Buddy</h2>
              <p>Let's plan your next adventure</p>
            </div>
            <div className="status-indicator">
              <span className="status-dot"></span>
              Online
            </div>
          </div>

          <div className="chat-messages">
            {chatMessages.map((message) => (
              <div key={message.id} className={`message ${message.type}`}>
                <div className="message-content">
                  <div className="message-text">{safeText(message.text)}</div>
                  {message.searchLinks && Object.keys(message.searchLinks).length > 0 && (
                    <div className="search-links-container">
                      <div className="search-links-buttons">
                        {message.searchLinks.aviasales && (
                          <a href={message.searchLinks.aviasales} target="_blank" rel="noopener noreferrer" className="search-link-btn">✈️ Aviasales</a>
                        )}
                        {message.searchLinks.kayak && (
                          <a href={message.searchLinks.kayak} target="_blank" rel="noopener noreferrer" className="search-link-btn">🔍 Kayak</a>
                        )}
                        {message.searchLinks.google && (
                          <a href={message.searchLinks.google} target="_blank" rel="noopener noreferrer" className="search-link-btn">🌐 Google Flights</a>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="message-timestamp">{safeText(message.timestamp)}</div>
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="message ai">
                <div className="message-content">
                  <div className="typing-indicator"><span></span><span></span><span></span></div>
                </div>
              </div>
            )}
            {isSearching && (
              <div className="message ai searching-message">
                <div className="message-content">
                  <div className="searching-indicator">
                    <div className="plane-loading">✈️</div>
                    <div className="searching-text">
                      <strong>Finding your flights...</strong>
                      <p>{safeText(flightInfo.from)} → {safeText(flightInfo.to)}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={chatMessagesEndRef} />
          </div>

          <div className="chat-input-area">
            <div className="input-wrapper">
              <input 
                type="text" 
                className="chat-input" 
                placeholder="Ask a follow-up..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              />
              <button className="voice-btn">🎤</button>
              <button className="send-btn" onClick={handleSendMessage}>↗</button>
            </div>
          </div>
        </div>

        {/* CENTER PANEL: Results */}
        <div className="results-panel">
          
          {/* Trip Builder */}
          {tripData && (
            <TripBuilder 
              tripData={tripData}
              onSearchSegment={handleSearchSegment}
              onSearchAll={handleSearchAllSegments}
              onSelectOption={handleSelectOption}
              onAddHotel={handleAddHotelToTrip}
              onClose={handleCloseTripBuilder}
              isSearching={isSearching}
              segmentResults={segmentResults}
              selectedOptions={selectedOptions}
            />
          )}

          {/* Itinerary View */}
          {itineraryData && currentUiHint === 'itinerary_view' && (
            <ItineraryView 
              itineraryData={itineraryData}
              message={itineraryMessage}
              onClose={handleCloseItinerary}
              onBookFlights={handleSearchFromItinerary}
              onBookHotels={() => {
                if (itineraryData?.destination) {
                  setChatInput(`Find hotels in ${itineraryData.destination}`);
                  setTimeout(() => document.querySelector('.send-btn')?.click(), 100);
                }
              }}
            />
          )}

          {/* ═══════════════════════════════════════════════════════════════════════════════
               ITINERARY BUILDER - Multi-City Trip Planning (Trip Type 5)
               ═══════════════════════════════════════════════════════════════════════════════ */}
          {showItineraryBuilder && itineraryData && (
            <div className="itinerary-builder-wrapper">
              <ItineraryBuilder
                itinerary={itineraryData}
                flights={allFlights}
                hotels={hotels}
                searchParams={{
                  origin: itineraryData?.origin || flightInfo?.from || userLocation?.iataCode || '',
                  destination: itineraryData?.cities?.[0]?.name || flightInfo?.to || '',
                  departureDate: itineraryData?.startDate || flightInfo?.departDate || '',
                  returnDate: itineraryData?.endDate || flightInfo?.returnDate || '',
                  passengers: itineraryData?.travelers || flightInfo?.passengers || 1
                }}
                onBook={(bookingData) => {
                  console.log('Itinerary booked:', bookingData);
                  trackEvent('itinerary_booking', { 
                    destinations: itineraryData?.cities?.map(c => c.name).join(', '),
                    total: bookingData?.total 
                  });
                }}
                onClose={() => {
                  setShowItineraryBuilder(false);
                  setItineraryData(null);
                  setCurrentUiHint('chat');
                }}
              />
            </div>
          )}

          {/* Recommendations View */}
          {recommendationsData && currentUiHint === 'recommendations_view' && (
            <RecommendationsView 
              recommendationsData={recommendationsData}
              message={recommendationsMessage}
              onClose={handleCloseRecommendations}
              onSelectDestination={handleSelectDestination}
              onSearchFlights={handleSearchFromRecommendation}
            />
          )}

          {/* ═══════════════════════════════════════════════════════════════════════════════
               PACKAGE BUILDER - Shows when BOTH flights AND hotels are available
               ═══════════════════════════════════════════════════════════════════════════════ */}
          {showPackageBuilder && allFlights.length > 0 && hotels.length > 0 && (
            <div className="package-builder-wrapper">
            <PackageBuilder
              flights={allFlights}
              hotels={hotels}
              searchParams={{
                origin: flightInfo?.from || '',
                destination: flightInfo?.to || '',
                departureDate: flightInfo?.departDate || '',
                returnDate: flightInfo?.returnDate || '',
                passengers: flightInfo?.passengers || 1,
                travelClass: flightInfo?.class || 'economy'
              }}
              hotelParams={{
                city: flightInfo?.to || '',
                checkIn: flightInfo?.departDate || '',
                checkOut: flightInfo?.returnDate || flightInfo?.departDate || '',
                guests: flightInfo?.passengers || 2
              }}
              onBook={(packageData) => {
                console.log('Package booked:', packageData);
                trackEvent('package_booking', { 
                  destination: flightInfo?.to,
                  total: packageData.totalPrice 
                });
              }}
              onClose={() => {
                setShowPackageBuilder(false);
                setCurrentUiHint('flight_results');
              }}
            />
            </div>
          )}

          {/* Flight Results with Filters - Only show when NOT in package builder mode */}
          {allFlights.length > 0 && !tripData && !itineraryData && !recommendationsData && !showPackageBuilder && (
            <div className="split-results-section">
              {/* Filters Sidebar */}
              <div className="filters-sidebar">
                <div className="filters-header">
                  <div className="filters-title"><span className="filters-title-icon">⚙️</span>Filters</div>
                  {hasActiveFilters() && <button className="clear-filters-btn" onClick={clearFilters}>Clear All</button>}
                </div>
                <div className="filter-group">
                  <div className="filter-group-header"><span className="filter-group-title">Price Range</span></div>
                  <div className="price-slider-container">
                    <div className="price-display"><span className="price-value">${priceRange[0]}</span><span className="price-separator">—</span><span className="price-value">${priceRange[1]}</span></div>
                    <input type="range" className="custom-range" min="0" max={Math.max(priceRange[1], 5000)} value={priceRange[1]} onChange={(e) => setPriceRange([0, Number(e.target.value)])} />
                  </div>
                </div>
                <div className="filter-divider"></div>
                <div className="filter-group">
                  <div className="filter-group-header"><span className="filter-group-title">Stops</span></div>
                  <div className="filter-options">
                    {['any', 'nonstop', '1stop'].map(s => (
                      <div key={s} className={`filter-option ${stops === s ? 'selected' : ''}`} onClick={() => setStops(s)}>
                        <div className="custom-radio"><div className="custom-radio-inner"></div></div>
                        <span className="option-label">{s === 'any' ? 'Any' : s === 'nonstop' ? 'Nonstop' : '1 Stop'}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="filter-divider"></div>
                <div className="filter-group">
                  <div className="filter-group-header"><span className="filter-group-title">Airlines</span></div>
                  <div className="filter-options airlines-filter">
                    {(() => {
                      const airlineSet = new Set();
                      allFlights.forEach(f => {
                        const name = typeof f.airline === 'object' ? f.airline.name : f.airline;
                        const code = typeof f.airline === 'object' ? f.airline.code : '';
                        if (name && name !== 'Unknown') airlineSet.add(name);
                        else if (code) airlineSet.add(code);
                      });
                      const uniqueAirlines = Array.from(airlineSet).sort();
                      
                      return uniqueAirlines.slice(0, 8).map(airlineName => (
                        <div 
                          key={airlineName} 
                          className={`filter-option checkbox-option ${airlines.includes(airlineName) ? 'selected' : ''}`}
                          onClick={() => toggleAirline(airlineName)}
                        >
                          <div className={`custom-checkbox ${airlines.includes(airlineName) ? 'checked' : ''}`}>
                            {airlines.includes(airlineName) && <span className="checkmark">✓</span>}
                          </div>
                          <span className="option-label">{airlineName}</span>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
                <div className="filter-divider"></div>
                <div className="filter-group">
                  <div className="filter-group-header"><span className="filter-group-title">Departure Time</span></div>
                  <div className="filter-options time-filter">
                    {[
                      { id: 'morning', label: 'Morning', sublabel: '5am - 12pm', icon: '🌅' },
                      { id: 'afternoon', label: 'Afternoon', sublabel: '12pm - 6pm', icon: '☀️' },
                      { id: 'evening', label: 'Evening', sublabel: '6pm - 12am', icon: '🌆' },
                      { id: 'night', label: 'Night', sublabel: '12am - 5am', icon: '🌙' }
                    ].map(time => (
                      <div 
                        key={time.id}
                        className={`filter-option time-option ${departureTime.includes(time.id) ? 'selected' : ''}`}
                        onClick={() => setDepartureTime(prev => 
                          prev.includes(time.id) ? prev.filter(t => t !== time.id) : [...prev, time.id]
                        )}
                      >
                        <div className={`custom-checkbox ${departureTime.includes(time.id) ? 'checked' : ''}`}>
                          {departureTime.includes(time.id) && <span className="checkmark">✓</span>}
                        </div>
                        <span className="time-icon">{time.icon}</span>
                        <div className="time-labels">
                          <span className="option-label">{time.label}</span>
                          <span className="option-sublabel">{time.sublabel}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Results Content */}
              <div className="results-content">
                <SearchSummaryBanner flightInfo={flightInfo} />
                <QuickActionsPanel flightInfo={flightInfo} flights={flights} onModify={handleModifySearch} />
                <div className="results-header">
                  <h3>{flights.length > 0 ? `${flights.length} Flights Found` : 'No Matching Flights'}</h3>
                  {flights.length > 0 && (
                    <div className="sort-options">
                      {['best', 'cheapest', 'fastest'].map(s => (
                        <button key={s} className={`sort-btn ${sortBy === s ? 'active' : ''}`} onClick={() => setSortBy(s)}>{s.charAt(0).toUpperCase() + s.slice(1)}</button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flights-list">
                  {flights.length === 0 && allFlights.length > 0 && (
                    <div className="no-matching-flights">
                      <div className="no-results-icon">🔍</div>
                      <h4>No matching flights found</h4>
                      <button className="clear-filters-btn-large" onClick={clearFilters}>Clear All Filters</button>
                    </div>
                  )}
                  {flights.length > 0 && isRoundTrip ? (
                    flights.map((flight, index) => (
                      <RoundTripFlightCard key={flight.id || `rt-${index}`} flight={flight} outboundFlight={flight.outboundFlight} returnFlight={flight.returnFlight} lastSearchParams={lastSearchParams || flightInfo} isBestDeal={index === 0} onBook={(data) => trackBookingClick(data, 'expedia')} />
                    ))
                  ) : flights.length > 0 ? (
                    flights.map((flight, index) => (
                      <OneWayFlightCard key={flight.id || `ow-${index}`} flight={flight} lastSearchParams={lastSearchParams || flightInfo} isBestDeal={index === 0} onBook={(data) => trackBookingClick(data.flight, 'expedia')} />
                    ))
                  ) : null}
                </div>
              </div>
            </div>
          )}

          {/* Hotels Section in Split Mode - Only show when NOT in package builder mode */}
        
            {hotels.length > 0 && !showPackageBuilder && (
  <section className="hotels-results-section split-hotels">
    <div className="section-container">
      <div className="hotels-header"><h2>🏨 Hotels</h2><span className="hotels-count">{hotels.length} options found</span></div>
      <HotelQuickOptions quickOptions={hotelQuickOptions} hotels={hotels} />

                <div className="hotels-list">
                  {hotels.filter(h => h && (h.name || h.hotelName)).map((hotel, index) => (
  <HotelCard key={hotel.id || `hotel-${index}`} hotel={{...hotel, name: safeText(hotel.name || hotel.hotelName, 'Hotel')}} isBestDeal={index === 0} onBook={(data) => trackEvent('hotel_booking_click', { hotel_name: safeText(hotel.name) })} />
))}
                </div>
              </div>
            </section>
          )}
        </div>

        {/* RIGHT PANEL: Ads */}
        <div className="ads-panel">
          <AdsSidebar />
        </div>
      </div>
      )}

      <SignInModal show={showSignIn} onClose={() => setShowSignIn(false)} onSignIn={handleOAuthSignIn} />
      <LimitReachedModal show={showLimitModal} onClose={() => setShowLimitModal(false)} usage={usageData} isGuest={!user} onUpgrade={() => { setShowLimitModal(false); window.location.href = '/pricing'; }} onSignIn={() => { setShowLimitModal(false); setShowSignIn(true); }} />
    </div>
  );
}

// Wrap App with Router for B2B support
function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/b2b/*" element={<B2BRouter />} />
        <Route path="/*" element={<App />} />
        
      </Routes>
    </BrowserRouter>
  );
}

export default AppRouter;
